import { Messages } from '../../../codegen/Localize';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '../../../commonInfra/contentActions/ActionMessages';
import { ContentActionNotification} from '../../../commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem, MultipleItemResult
} from '../../../commonInfra/contentActions/MultipleItemResult';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { CommonErrorCode, UserErrorCode } from '../../../ts/helpers/ErrorCodes';
import { L10n } from '../../../ts/L10n/L10n';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { Set } from '../../../ts/models/Set';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { NotificationHelper } from '../../components/helpers/NotificationHelper';
import { LicenseTierFilter } from '../filters/LicenseTierFilter';
import { filter } from '../filters/SiteRoleFilter';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';

// Notification For:
// => single or multiple server admin update

export class ServerAdminUpdateActionNotification {

  private siteRolesOptionsResource: SiteRoleOptionsResource;

  public static readonly instance = new ServerAdminUpdateActionNotification();

  public notify(
    result: IResult,
    users: Set<IUserInfo>,
    newAdminState: boolean) {

    this.siteRolesOptionsResource = SiteRoleOptionsResource.instance;

    const successfulName = NotificationHelper.getSuccessfulName(users, result);
    const failingName = NotificationHelper.getFailingName(users, result);

    // create a notification object for result reporting
    const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          const messageArgs = {
            USER_COUNT: successCount,
            USER_NAME: L10n.elideIfLong(successfulName),
            USER_COUNT_LOC: L10n.localizedCount(successCount)
          };
          return newAdminState ? Messages.serverAdminPromotionSuccess(messageArgs) : Messages.serverAdminDemotionSuccess(messageArgs);
        },
        describeErrorSummary: (errorCount: number) => {
          const messageArgs = {
            USER_COUNT: errorCount,
            USER_NAME: L10n.elideIfLong(failingName),
            USER_COUNT_LOC: L10n.localizedCount(errorCount)
          };
          return newAdminState ? Messages.serverAdminPromotionFailed(messageArgs) : Messages.serverAdminDemotionFailed(messageArgs);
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: NotificationHelper.getUrlForUser(<IUserInfo>errorItem.item),
            errorMessage: this.errorMessageFor(errorItem)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    ContentActionNotification.displayItemizedToastsForResult(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<IUserInfo>) {
    switch (errorItem.error.code) {
      case UserErrorCode.UserInsufficientLicenses:
        const translatedRole = filter(SiteRoles.ServerAdministrator);
        const licenseTier = this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(SiteRoles.ServerAdministrator).licenseTierRole;
        const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);

        return Messages.errorUserInsufficientLicenses_updateSiteRole({ SITE_ROLE: translatedRole, LICENSE_TIER: translatedLicenseRole });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.errorCallingUserInsufficientPermissions_serverAdminChange();
      case CommonErrorCode.SystemUserNotFound:
        return Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(errorItem.item.username) });
      case UserErrorCode.UserNotSupportedGuestOperation:
        return Messages.errorUserNotSupportedGuestOperation();
      case UserErrorCode.UserNotSupportedSelfOperation:
        return Messages.errorUserNotSupportedSelfOperation();
      case UserErrorCode.UserInsufficientSiteQuota:
        return Messages.errorUserInsufficientSiteQuota();
      default:
        return Messages.errorUnknown();
    }
  }

}

export default ServerAdminUpdateActionNotification.instance;
